import { Box } from '@mui/material';
import { CompanyBenefitStats } from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../hooks/useStore';
import { BenefitIcon } from '../../lib/components/compound/dataDisplay';
import { Carousel } from '../../lib/components/compound/surfaces/carousel';
import { BenefitItemProps } from './shared';
import { AnniversariesDashboardWidget } from './widgets/anniversaries';
import { BenefitActivatedDashboardWidget } from './widgets/benefitActivated';
import { BenefitMixCard } from './widgets/benefitMixCard';
import { BenefitWishesDashboardWidget } from './widgets/benefitWishes';
import { BirthdaysDashboardWidget } from './widgets/birthdays';
import { Page } from '../../lib/components';
// import { NewsDashboardWidget } from './widgets/news';
// import { SachbezugStatusWidget } from './widgets/sachbezugStatus';

const Dashboard = () => {
  const { benefitsStore, companyStore } = useStore();
  const { t } = useTranslation();

  const employeeCount = useMemo(() => benefitsStore.stats?.employee_count, [benefitsStore.stats]);
  const stats = useMemo<Array<BenefitItemProps>>(() => {
    const tempStats: Array<BenefitItemProps> = [];

    for (const stat in benefitsStore.stats) {
      if (stat === 'employee_count') continue;

      if (isKeyofCompanyBenefitStats(stat)) {
        tempStats.push({ ...benefitsStore.stats[stat], key: stat });
      }
    }

    return tempStats;
  }, [benefitsStore.stats]);

  useEffect(() => {
    if (!companyStore.company) return;

    const abortController = new AbortController();
    const fetch = async () => {
      try {
        await benefitsStore.fetchBenefitsStats();
        await companyStore.fetchBenefitSettings();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };

    fetch();

    return () => abortController.abort();
  }, [companyStore.company]);

  return (
    <Page>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridAutoRows: 'max-content',
          gap: '2rem',
          alignItems: 'start',
          overflow: 'overlay',
          paddingBottom: '2rem',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        <Box gridColumn="1 / span 2">
          <Carousel
            maxVisibleSlides={4}
            minimumItemWidth={212}
            isPlaying={false}
            items={stats
              .sort((a, b) => b.desired_percent - a.desired_percent)
              .map((stat, index) => (
                <BenefitMixCard
                  key={index}
                  title={t(`benefits:${stat.key}`)}
                  description={t(`benefits:${stat.key}_subtitle`)}
                  icon={<BenefitIcon benefitKey={stat.key} sx={{ marginX: 'auto' }} />}
                  percentValue={Math.round(stat.desired_percent)}
                />
              ))}
          />
        </Box>
        <BenefitWishesDashboardWidget stats={stats} employeeCount={employeeCount} />
        <BenefitActivatedDashboardWidget stats={stats} />
        <BirthdaysDashboardWidget />
        <AnniversariesDashboardWidget />
      </Box>
    </Page>
  );
};

const isKeyofCompanyBenefitStats = (
  key: string
): key is keyof Omit<CompanyBenefitStats, 'employee_count'> => key !== undefined;

export default observer(Dashboard);
